import { supabase } from '../../config/supabase';

/**
 * Get Australian date
 */
const getAustralianDate = () => {
    return new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
};

const applyCancelledFilter = (query, filters = {}) => {
    const cancelledFilter = filters.cancelledFilter || 'active_only';

    if (cancelledFilter === 'cancelled_only') {
        return query.eq('is_cancelled', true);
    }

    if (cancelledFilter === 'active_cancelled') {
        return query;
    }

    return query.or('is_cancelled.is.null,is_cancelled.eq.false');
};
/**
 * Helper function to get week and day from date
 */
const getWeekAndDay = (dateString) => {
    const targetDate = new Date(dateString);
    const today = getAustralianDate();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + 1);
    monday.setHours(0, 0, 0, 0);

    const diffTime = targetDate - monday;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const weekNumber = Math.floor(diffDays / 7) + 1;
    const dayOfWeek = targetDate.getDay();

    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const day = dayNames[dayOfWeek];

    // Only return if within 4 weeks and valid day
    if (weekNumber >= 1 && weekNumber <= 4 && dayOfWeek >= 1 && dayOfWeek <= 6) {
        return {
            week: `week${weekNumber}`,
            day: day
        };
    }

    return null;
};

/**
 * Fetch scheduled assignments for a specific week and schedule type
 */
export const getScheduledAssignments = async (companyId, scheduleType, filters = {}) => {
    try {
        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(*),
                job_designers(*),
                job_estimators(*, estimator_user:users!job_estimators_user_id_fkey(username, full_name)),
                job_production(*),
                job_deliveries(*)
            `)
            .eq('company_id', companyId)
            .eq('deleted', false);

        // Apply schedule type specific filters
        if (scheduleType === 'editor') {
            query = query.not('expected_estimation_finish_date', 'is', null);
        } else if (scheduleType === 'production') {
            query = query.not('booking_delivery_date', 'is', null);
        } else if (scheduleType === 'delivery') {
            // For delivery: must have a booking date AND be ready_for_delivery status
            query = query
                .not('booking_delivery_date', 'is', null)
                .eq('main_status', 'ready_for_delivery');
        } else {
            // For specific quote types (roof, wall, floor)
            query = query.not('expected_estimation_finish_date', 'is', null);
        }

        // Apply user filters based on schedule type
        if (filters.userId) {
            if (scheduleType === 'editor' || ['roof', 'wall', 'floor'].includes(scheduleType)) {
                // For design schedules, check if user is assigned as designer
                const { data: designerJobs } = await supabase
                    .from('job_designers')
                    .select('job_id')
                    .eq('user_id', filters.userId);

                const jobIds = designerJobs?.map(d => d.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    // No jobs found for this user
                    return {
                        week1: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week2: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week3: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week4: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] }
                    };
                }
            } else if (scheduleType === 'production') {
                // For production, check production assignments
                const { data: prodJobs } = await supabase
                    .from('job_production')
                    .select('job_id')
                    .eq('user_id', filters.userId);

                const jobIds = prodJobs?.map(p => p.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    return {
                        week1: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week2: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week3: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week4: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] }
                    };
                }
            } else if (scheduleType === 'delivery') {
                // For delivery, filter by assigned driver
                const { data: deliveryJobs } = await supabase
                    .from('job_deliveries')
                    .select('job_id')
                    .eq('assigned_driver_id', filters.userId);

                const jobIds = deliveryJobs?.map(d => d.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    return {
                        week1: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week2: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week3: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week4: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] }
                    };
                }
            }
        }

        const { data, error } = await query;

        if (error) throw error;

        // Organize data by weeks and days
        const scheduleData = {
            week1: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
            week2: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
            week3: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
            week4: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] }
        };

        // Process and categorize jobs
        data?.forEach(job => {
            const targetDate = scheduleType === 'editor' || ['roof', 'wall', 'floor'].includes(scheduleType)
                ? job.expected_estimation_finish_date
                : job.booking_delivery_date;

            if (targetDate) {
                const weekDay = getWeekAndDay(targetDate);
                if (weekDay && scheduleData[weekDay.week] && scheduleData[weekDay.week][weekDay.day]) {
                    scheduleData[weekDay.week][weekDay.day].push(transformJobData(job));
                }
            }
        });

        return scheduleData;
    } catch (error) {
        console.error('Error fetching scheduled assignments:', error);
        throw error;
    }
};

/**
 * Fetch unscheduled assignments
 */
export const getUnscheduledAssignments = async (companyId, scheduleType, filters = {}) => {
    try {
        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(*),
                job_designers(*),
                job_estimators(*, estimator_user:users!job_estimators_user_id_fkey(username, full_name)),
                job_production(*),
                job_deliveries(*)
            `)
            .eq('company_id', companyId)
            .eq('deleted', false);

        // Filter for unscheduled based on schedule type
        if (scheduleType === 'editor' || ['roof', 'wall', 'floor'].includes(scheduleType)) {
            query = query.is('expected_estimation_finish_date', null);
        } else if (scheduleType === 'production') {
            query = query.is('booking_delivery_date', null);
        } else if (scheduleType === 'delivery') {
            // For delivery: must be ready_for_delivery AND no booking date set
            query = query
                .eq('main_status', 'ready_for_delivery')
                .is('booking_delivery_date', null);
        }

        // Apply user filters
        if (filters.userId) {
            if (scheduleType === 'editor' || ['roof', 'wall', 'floor'].includes(scheduleType)) {
                const { data: designerJobs } = await supabase
                    .from('job_designers')
                    .select('job_id')
                    .eq('user_id', filters.userId);

                const jobIds = designerJobs?.map(d => d.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    return [];
                }
            } else if (scheduleType === 'production') {
                const { data: prodJobs } = await supabase
                    .from('job_production')
                    .select('job_id')
                    .eq('user_id', filters.userId);

                const jobIds = prodJobs?.map(p => p.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    return [];
                }
            } else if (scheduleType === 'delivery') {
                // For delivery, filter by assigned driver
                const { data: deliveryJobs } = await supabase
                    .from('job_deliveries')
                    .select('job_id')
                    .eq('assigned_driver_id', filters.userId);

                const jobIds = deliveryJobs?.map(d => d.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    return [];
                }
            }
        }

        const { data, error } = await query;

        if (error) throw error;

        return data?.map(transformJobData) || [];
    } catch (error) {
        console.error('Error fetching unscheduled assignments:', error);
        throw error;
    }
};

// Add this function if it doesn't exist or update it
export const getEditorTableData = async (companyId, scheduleType, page = 1, limit = 50, filters = {}) => {
    try {
        const offset = (page - 1) * limit;

        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(*),
                job_designers(*),
                job_estimators(*),
                job_production(*),
                job_deliveries(*)
            `, { count: 'exact' })
            .eq('company_id', companyId)
            .eq('deleted', false);

        // If jobId filter is provided, fetch only that specific job
        if (filters.jobId) {
            query = query.eq('id', filters.jobId);
        }

        // Apply job type filter if provided
        if (filters.jobTypeFilter && filters.jobTypeFilter !== 'all') {
            if (filters.jobTypeFilter === 'design_only') {
                query = query.eq('job_type', 'Design');
            } else if (filters.jobTypeFilter === 'estimation_only') {
                query = query.eq('job_type', 'Estimation');
            } else if (filters.jobTypeFilter === 'design_estimation_only') {
                query = query.eq('job_type', 'Design & Estimation');
            } else if (filters.jobTypeFilter === 'timber_only') {
                query = query.eq('job_type', 'Timber');
            }
        }

        // Apply urgent filter if provided
        if (filters.urgentFilter && filters.urgentFilter !== 'all') {
            if (filters.urgentFilter === 'urgent_quote') {
                query = query.eq('urgent_quote', true);
            } else if (filters.urgentFilter === 'urgent_design') {
                query = query.eq('urgent_design', true);
            }
        }

        // Apply quote sent filter if provided
        if (filters.quoteSentFilter && filters.quoteSentFilter !== 'all') {
            if (filters.quoteSentFilter === 'sent') {
                query = query.eq('quote_sent', true);
            } else if (filters.quoteSentFilter === 'not_sent') {
                query = query.eq('quote_sent', false);
            }
        }

        // Collect all job IDs to exclude from multiple hide-filters into one Set,
        // then apply a single .not('id','in',...) call. Multiple .not() calls on the
        // same column produce duplicate query params that PostgREST may handle
        // inconsistently (only last wins, or 0 results), so we merge them here.
        const jobIdsToExclude = new Set();

        if (filters.hideEstimationCompleted) {
            const { data: estimationQuotes } = await supabase
                .from('job_quotes')
                .select('job_id, jobs!inner(company_id)')
                .eq('jobs.company_id', companyId)
                .eq('estimation_status', 'completed');
            estimationQuotes?.forEach(q => jobIdsToExclude.add(q.job_id));
        }

        if (filters.hideEstimatorAssigned) {
            // No company join needed — main query is already company-scoped.
            const { data: assignedEstimators } = await supabase
                .from('job_estimators')
                .select('job_id')
                .limit(10000);
            assignedEstimators?.forEach(e => jobIdsToExclude.add(e.job_id));
        }

        if (jobIdsToExclude.size > 0) {
            query = query.not('id', 'in', `(${[...jobIdsToExclude].join(',')})`);
        }

        // Apply sorting based on sortBy filter
        const sortBy = filters.sortBy || 'newest';
        if (sortBy === 'oldest') {
            query = query.order('created_at', { ascending: true });
        } else if (sortBy === 'priority') {
            // Order by urgent_quote DESC, urgent_design DESC, then by expected_estimation_finish_date ASC (earliest first), nulls last
            query = query
                .order('urgent_quote', { ascending: false, nullsFirst: false })
                .order('urgent_design', { ascending: false, nullsFirst: false })
                .order('created_at', { ascending: true });
        } else if (sortBy === 'alphabetical') {
            query = query
                .order('uid_prefix', { ascending: true })
                .order('uid_number', { ascending: true, nullsLast: true });
        } else if (sortBy === 'reverse_alphabetical') {
            query = query
                .order('uid_prefix', { ascending: false })
                .order('uid_number', { ascending: false, nullsFirst: true });
        }
        else {
            // Default: newest first
            query = query.order('created_at', { ascending: false });
        }

        query = query.range(offset, offset + limit - 1);

        // Apply user filters
        if (filters.userId) {
            if (scheduleType === 'editor' || ['roof', 'wall', 'floor'].includes(scheduleType)) {
                const { data: designerJobs } = await supabase
                    .from('job_designers')
                    .select('job_id')
                    .eq('user_id', filters.userId);

                const jobIds = designerJobs?.map(d => d.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    return {
                        data: [],
                        pagination: { page, limit, total: 0, hasMore: false }
                    };
                }
            } else if (scheduleType === 'production') {
                const { data: prodJobs } = await supabase
                    .from('job_production')
                    .select('job_id')
                    .eq('user_id', filters.userId);

                const jobIds = prodJobs?.map(p => p.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    return {
                        data: [],
                        pagination: { page, limit, total: 0, hasMore: false }
                    };
                }
            }
        }

        const { data, error, count } = await query;




        if (error) throw error;

        // Transform data first
        let transformedData = data?.map(transformJobData) || [];


        // Apply custom sorting for priority if needed
        if (sortBy === 'priority' && transformedData.length > 0) {
            transformedData = transformedData.sort((a, b) => {
                // Helper function to get earliest date
                const getEstimationDate = (job) => {
                    if (job.expectedEstimationFinishDate || job.expected_estimation_finish_date) {
                        return new Date(job.expectedEstimationFinishDate || job.expected_estimation_finish_date);
                    }
                    return null;
                };

                // Determine urgency levels
                const aIsUrgentQuote = a.jobInformation?.urgentQuote || false;
                const bIsUrgentQuote = b.jobInformation?.urgentQuote || false;
                const aIsUrgentDesign = a.jobInformation?.urgentDesign || false;
                const bIsUrgentDesign = b.jobInformation?.urgentDesign || false;

                // Both urgent quote: sort by date
                if (aIsUrgentQuote && bIsUrgentQuote) {
                    const dateA = getEstimationDate(a);
                    const dateB = getEstimationDate(b);

                    // Both have estimation dates
                    if (dateA && dateB) {
                        return dateA.getTime() - dateB.getTime();
                    }

                    // If neither has estimation date, sort by oldest first (created_at)
                    if (dateA === null && dateB === null) {
                        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
                    }

                    // One has date, one doesn't - prioritize the one with date
                    if (dateA === null) return 1;
                    if (dateB === null) return -1;
                }

                // One is urgent quote, other is not: urgent quote comes first
                if (aIsUrgentQuote !== bIsUrgentQuote) {
                    return bIsUrgentQuote ? 1 : -1;
                }

                // Both urgent design (but not urgent quote): sort by date
                if (aIsUrgentDesign && bIsUrgentDesign) {
                    const dateA = getEstimationDate(a);
                    const dateB = getEstimationDate(b);

                    // Both have estimation dates
                    if (dateA && dateB) {
                        return dateA.getTime() - dateB.getTime();
                    }

                    // One has date, one doesn't - prioritize the one with date
                    if (dateA === null) return 1;
                    if (dateB === null) return -1;
                }

                // One is urgent design, other is not: urgent design comes first
                if (aIsUrgentDesign !== bIsUrgentDesign) {
                    return bIsUrgentDesign ? 1 : -1;
                }

                // Neither urgent: sort by date
                const dateA = getEstimationDate(a);
                const dateB = getEstimationDate(b);

                // Both have estimation dates
                if (dateA && dateB) {
                    return dateA.getTime() - dateB.getTime();
                }

                // One has date, one doesn't - prioritize the one with date
                if (dateA === null) return 1;
                if (dateB === null) return -1;

                return 0;
            });
        }

        return {
            data: transformedData,
            pagination: {
                page,
                limit,
                total: count || 0,
                hasMore: count ? (page * limit) < count : false
            }
        };
    } catch (error) {
        console.error('Error fetching editor table data:', error);
        throw error;
    }
};

export const getEditorTableDataRPC = async (companyId, scheduleType, page = 1, limit = 50, filters = {}) => {
    try {
        const offset = (page - 1) * limit;

        const { data: idRows, error: idError } = await supabase.rpc('get_editor_table_job_ids', {
            p_company_id: companyId,
            p_user_id: filters.userId || null,
            p_job_id: filters.jobId || null,
            p_job_type_filter: filters.jobTypeFilter || 'all',
            p_urgent_filter: filters.urgentFilter || 'all',
            p_quote_sent_filter: filters.quoteSentFilter || 'all',
            p_hide_estimation_completed: !!filters.hideEstimationCompleted,
            p_hide_estimator_assigned: !!filters.hideEstimatorAssigned,
            p_sort_by: filters.sortBy || 'newest',
            p_limit: limit,
            p_offset: offset,
            p_cancelled_filter: filters.cancelledFilter || 'active_only',
            p_quote_types: filters.quoteTypes ?? null,
            p_invoice_filter: filters.invoiceFilter || 'all',
        });

        if (idError) throw idError;

        const jobIds = (idRows || []).map(r => r.job_id);
        const total = Number(idRows?.[0]?.total_count || 0);

        if (jobIds.length === 0) {
            return {
                data: [],
                pagination: {
                    page,
                    limit,
                    total,
                    hasMore: false,
                },
            };
        }

        const { data, error } = await supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(*),
                job_designers(*),
                job_estimators(*),
                job_production(*),
                job_deliveries(*)
            `)
            .in('id', jobIds);

        if (error) throw error;

        const byId = new Map((data || []).map(job => [job.id, job]));
        const orderedData = jobIds
            .map(id => byId.get(id))
            .filter(Boolean)
            .map(transformJobData);

        return {
            data: orderedData,
            pagination: {
                page,
                limit,
                total,
                hasMore: offset + orderedData.length < total,
            },
        };
    } catch (error) {
        console.error('Error fetching editor table data:', error);
        throw error;
    }
};

/**
 * Transform job data from snake_case to camelCase
 */
const transformJobData = (job) => {
    // Transform quotes
    const quotes = {};
    if (job.job_quotes) {
        // Pre-count how many required quotes exist per type.
        // Used to decide whether the quote_type fallback is safe for designers
        // that have no child_job_id (old-style single-quote-per-type jobs).
        const requiredCountByType = {};
        job.job_quotes.forEach(q => {
            if (q.is_required && !(q.delivery_count > 0)) {
                requiredCountByType[q.quote_type] = (requiredCountByType[q.quote_type] || 0) + 1;
            }
        });

        job.job_quotes.forEach(quote => {

            if (quote.delivery_count || quote.delivery_count > 0) return;
            // Determine designers for this specific quote entry:
            //  1. child_job_id is set → designer explicitly linked to this quote UUID (new style)
            //  2. child_job_id is null + only ONE quote of this type → safe to fall back to
            //     quote_type match (old single-quote-per-type jobs)
            //  3. child_job_id is null + multiple quotes of this type → skip; can't safely
            //     assign an unlinked designer to any specific child — would duplicate across all.
            const isMultiType = (requiredCountByType[quote.quote_type] || 0) > 1;
            const designers = job.job_designers
                ?.filter(d => {
                    if (d.child_job_id) return d.child_job_id === quote.id;
                    if (!isMultiType) return d.quote_type === quote.quote_type;
                    return false;
                })
                ?.map(d => ({
                    userId: d.user_id,
                    name: d.name,
                    isStarted: d.is_started,
                    isCompleted: d.is_completed
                })) || [];

            quotes[quote.id] = {
                id: quote.id,
                isRequired: quote.is_required,
                on_hold: quote.on_hold || false,
                layout_only: quote.layout_only || false,
                waiting_job: quote.waiting_job || false,
                sent_production: quote.sent_production || false,
                designStatus: quote.design_status,
                estimationStatus: quote.estimation_status,
                invoiced: quote.invoiced || false,

                invoice_state: quote.invoice_state || quote.invoice_status || 'not_invoiced',
                invoice_status: quote.invoice_status || quote.invoice_state || 'not_invoiced',

                xero_invoice_status: quote.xero_invoice_status || null,
                xero_invoice_id: quote.xero_invoice_id || null,
                xero_invoice_number: quote.xero_invoice_number || null,
                xero_last_synced_at: quote.xero_last_synced_at || null,

                expectedFinishDate: quote.expected_finish_date,
                expected_finish_date: quote.expected_finish_date,
                expectedDeliveryDate: quote.expected_delivery_date,
                expected_delivery_date: quote.expected_delivery_date,
                child_unique_id: quote?.child_unique_id || null,
                quote_type: quote?.quote_type,

                department_file_status: quote.department_file_status || "not_required",
                departmentFileStatus: quote.department_file_status || "not_required",
                department_files_completed_at: quote.department_files_completed_at || null,
                departmentFilesCompletedAt: quote.department_files_completed_at || null,
                department_files_completed_by_name: quote.department_files_completed_by_name || null,

                editorNote: quote.editor_note || [],
                designers: designers,
                production: job.job_production?.find(p => p.quote_type === quote.quote_type) ? {
                    phase: job.job_production.find(p => p.quote_type === quote.quote_type).phase,
                    userId: job.job_production.find(p => p.quote_type === quote.quote_type).user_id,
                    name: job.job_production.find(p => p.quote_type === quote.quote_type).name,
                    isStarted: job.job_production.find(p => p.quote_type === quote.quote_type).is_started_production,
                    isCompleted: job.job_production.find(p => p.quote_type === quote.quote_type).is_completed,
                    startCuttingAt: job.job_production.find(p => p.quote_type === quote.quote_type).start_cutting_at,
                    endCuttingAt: job.job_production.find(p => p.quote_type === quote.quote_type).end_cutting_at,
                    startProductionAt: job.job_production.find(p => p.quote_type === quote.quote_type).start_production_at,
                    endProductionAt: job.job_production.find(p => p.quote_type === quote.quote_type).end_production_at,
                } : null,
                delivery: job.job_deliveries?.find(d => d.quote_type === quote.quote_type) ? {
                    isRequired: job.job_deliveries.find(d => d.quote_type === quote.quote_type).is_required,
                    deliveryStatus: job.job_deliveries.find(d => d.quote_type === quote.quote_type).delivery_status,
                    deliveryDate: job.job_deliveries.find(d => d.quote_type === quote.quote_type).delivery_date,
                    assignedDriver: job.job_deliveries.find(d => d.quote_type === quote.quote_type).assigned_driver_id ? {
                        driverId: job.job_deliveries.find(d => d.quote_type === quote.quote_type).assigned_driver_id,
                        driverName: job.job_deliveries.find(d => d.quote_type === quote.quote_type).assigned_driver_name,
                        assignedAt: job.job_deliveries.find(d => d.quote_type === quote.quote_type).assigned_at,
                        assignedBy: job.job_deliveries.find(d => d.quote_type === quote.quote_type).assigned_by_name ? {
                            name: job.job_deliveries.find(d => d.quote_type === quote.quote_type).assigned_by_name
                        } : null
                    } : null,
                    deliveryNotes: job.job_deliveries.find(d => d.quote_type === quote.quote_type).delivery_notes,
                    // child_id: job.job_deliveries.find(d => d.child_id === quote.id).child_id || null
                } : null
            };
        });
    }

    // Transform estimator
    const estimator = job?.job_estimators || null

    return {
        _id: job.id,
        uniqueId: job.unique_id,
        customerName: job.customer_name,
        customerNumber: job.customer_number,
        customerMail: job.customer_mail,
        carpenterSupervisorName: job.carpenter_supervisor_name,
        customerAddress: job.customer_address,
        jobType: job.job_type,
        mainStatus: job.main_status,
        currentStatus: job.current_status,
        estimationFinishDate: job.expected_estimation_finish_date,
        expectedEstimationFinishDate: job.expected_estimation_finish_date,
        expected_estimation_finish_date: job.expected_estimation_finish_date,
        bookingDeliveryDate: job.booking_delivery_date,
        booking_delivery_date: job.booking_delivery_date,
        invoiced: job.invoiced,
        quote_sent: job.quote_sent || false,
        quoteSent: job.quote_sent || false,
        jobInformation: {
            urgentQuote: job.urgent_quote,
            urgentDesign: job.urgent_design
        },
        quotes: quotes,
        estimator: estimator,
        editorNote: job.editor_note || [],
        createdAt: job.created_at,
        updatedAt: job.updated_at,
        on_hold: job.on_hold,
        is_cancelled: job.is_cancelled,
        isCancelled: job.is_cancelled,
    };
};

/**
 * Update job schedule date
 * @param {string} jobId - The job ID
 * @param {string} quoteType - The quote type
 * @param {Date|null} newDate - The new date or null to unschedule
 * @returns {Promise<Object>} Result of the update
 */
export const updateJobSchedule = async (jobId, quoteType, newDate) => {
    try {
        const updateData = {
            expected_estimation_finish_date: newDate ? newDate.toISOString() : null,
            updated_at: new Date().toISOString()
        };

        const { data, error } = await supabase
            .from('jobs')
            .update(updateData)
            .eq('id', jobId)
            .select() // Add select() to return the updated row
            .single(); // Expect a single row

        if (error) throw error;

        return { data, error: null };
    } catch (error) {
        console.error('Error updating job schedule:', error);
        return { data: null, error };
    }
};

/**
 * Update editor_note array on the jobs table
 */
export const updateJobEditorNote = async (jobId, notes) => {
    const { error } = await supabase
        .from('jobs')
        .update({ editor_note: notes })
        .eq('id', jobId);
    if (error) throw error;
};

/**
 * Toggle timber_is_checked on a specific job_quotes row by UUID.
 */
export const toggleTimberIsChecked = async (quoteId, newValue) => {
    const { error } = await supabase
        .from('job_quotes')
        .update({ timber_is_checked: newValue })
        .eq('id', quoteId);
    if (error) throw error;
};

/**
 * Update quick_notes JSONB array on a specific job_quotes row.
 * Each note: { id, note, added_by_id, added_by_name, added_at, edited_at }
 */
export const updateQuoteQuickNotes = async (quoteId, notes) => {
    const { error } = await supabase
        .from('job_quotes')
        .update({ quick_notes: notes })
        .eq('id', quoteId);
    if (error) throw error;
};

/**
 * Update editor_note array on the job_quotes table
 */
export const updateQuoteEditorNote = async (quoteId, notes) => {
    const { error } = await supabase
        .from('job_quotes')
        .update({ editor_note: notes })
        .eq('id', quoteId);
    if (error) throw error;
};

/**
 * Fetch available users (designers and estimators)
 */
export const getAvailableUsers = async (companyId) => {
    try {
        const { data, error } = await supabase
            .from('users')
            .select('id, username, full_name, role')
            .eq('company_id', companyId)
            .in('role', ['designer', 'estimator'])
            .eq('status', 'active');

        if (error) throw error;

        // Organize by role
        const designers = data?.filter(u => u.role === 'designer').map(u => ({
            userId: u.id,
            username: u.username || u.full_name
        })) || [];

        const estimators = data?.filter(u => u.role === 'estimator').map(u => ({
            userId: u.id,
            username: u.username || u.full_name
        })) || [];

        return { designers, estimators };
    } catch (error) {
        console.error('Error fetching available users:', error);
        throw error;
    }
};

/**
 * Get production table data with pagination and filtering
 * @param {string} companyId - The company ID
 * @param {string} scheduleType - Type of schedule (production, editor, etc.)
 * @param {number} page - Current page number
 * @param {number} limit - Items per page
 * @param {Object} filters - Filter options (userId, etc.)
 * @returns {Promise<Object>} Production data and pagination info
 */
export const getProductionTableData = async (companyId, scheduleType, page = 1, limit = 50, filters = {}) => {
    try {
        const offset = (page - 1) * limit;

        // Main query with inner join to get ONLY production-ready jobs
        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes!inner (
                    id,
                    quote_type,
                    is_required,
                    design_status,
                    estimation_status,
                    estimation_value,
                    estimation_notes,
                    expected_finish_date,
                    delivery_count,
                    production_status
                ),
                job_production (
                    id,
                    quote_type,
                    user_id,
                    name,
                    phase,
                    is_started_cutting,
                    is_started_production,
                    is_completed,
                    start_cutting_at,
                    end_cutting_at,
                    start_production_at,
                    end_production_at,
                    production_notes
                ),
                job_designers (
                    id,
                    quote_type,
                    user_id,
                    name,
                    assigned_at,
                    started_at,
                    completed_at,
                    is_started,
                    is_completed,
                    finishing_note
                ),
                job_estimators (
                    id,
                    user_id,
                    name,
                    assigned_at,
                    started_at,
                    completed_at,
                    is_started,
                    is_completed,
                    finishing_note,
                    estimator_user:users!job_estimators_user_id_fkey(username, full_name)
                ),
                job_deliveries (
                    id,
                    quote_type,
                    is_required,
                    delivery_date,
                    delivery_status,
                    assigned_driver_id,
                    assigned_driver_name
                )
            `, { count: 'exact' })
            .eq('company_id', companyId)
            .eq('deleted', false)
            .in('job_type', ['Design', 'Design & Estimation'])
            .eq('job_quotes.is_required', true)
            .eq('job_quotes.design_status', 'completed')
            .or('delivery_count.is.null,delivery_count.eq.0', { foreignTable: 'job_quotes' });

        // If jobId filter is provided, fetch only that specific job
        if (filters.jobId) {
            query = query.eq('id', filters.jobId);
        }

        // Apply job type filter if provided
        if (filters.jobTypeFilter && filters.jobTypeFilter !== 'all') {
            if (filters.jobTypeFilter === 'design_only') {
                query = query.eq('job_type', 'Design');
            } else if (filters.jobTypeFilter === 'estimation_only') {
                query = query.eq('job_type', 'Estimation');
            } else if (filters.jobTypeFilter === 'design_estimation_only') {
                query = query.eq('job_type', 'Design & Estimation');
            } else if (filters.jobTypeFilter === 'timber_only') {
                query = query.eq('job_type', 'Timber');
            }
        }

        // Apply urgent filter if provided
        if (filters.urgentFilter && filters.urgentFilter !== 'all') {
            if (filters.urgentFilter === 'urgent_quote') {
                query = query.eq('urgent_quote', true);
            } else if (filters.urgentFilter === 'urgent_design') {
                query = query.eq('urgent_design', true);
            }
        }

        // Apply sorting based on sortBy filter
        const sortBy = filters.sortBy || 'newest';
        if (sortBy === 'oldest') {
            query = query.order('created_at', { ascending: true });
        } else if (sortBy === 'priority') {
            // Order by urgent_quote DESC, urgent_design DESC, then by booking_delivery_date ASC (earliest first), nulls last
            query = query
                .order('urgent_quote', { ascending: false, nullsFirst: false })
                .order('urgent_design', { ascending: false, nullsFirst: false })
        } else if (sortBy === 'alphabetical') {
            query = query
                .order('uid_prefix', { ascending: true })
                .order('uid_number', { ascending: true, nullsLast: true });
        } else if (sortBy === 'reverse_alphabetical') {
            query = query
                .order('uid_prefix', { ascending: false })
                .order('uid_number', { ascending: false, nullsFirst: true });
        } else {
            query = query.order('created_at', { ascending: false });
        }

        // Apply pagination BEFORE executing query (like getEditorTableData)
        query = query.range(offset, offset + limit - 1);

        // Apply user filter if provided
        if (filters.userId) {
            query = query.or(`job_designers.user_id.eq.${filters.userId},job_estimators.user_id.eq.${filters.userId}`);
        }

        const { data, error, count } = await query;

        if (error) throw error;


        // Transform the data to match the expected format
        const transformedData = data?.map(job => {
            // Filter job_quotes to only include initial quotes (delivery_count is null, undefined, or 0)
            const initialQuotes = job.job_quotes?.filter(quote =>
                quote.delivery_count === null || quote.delivery_count === undefined || quote.delivery_count === 0
            ) || [];

            // Group quotes by type
            const quotes = {};
            initialQuotes.forEach(quote => {
                const quoteType = quote.quote_type;

                // Get designers for this quote type
                const designers = job.job_designers
                    ?.filter(d => d.quote_type === quoteType)
                    .map(d => ({
                        userId: d.user_id,
                        name: d.name,
                        assignedAt: d.assigned_at,
                        startedAt: d.started_at,
                        completedAt: d.completed_at,
                        isStarted: d.is_started,
                        isCompleted: d.is_completed,
                        finishingNote: d.finishing_note
                    })) || [];

                // Get production data for this quote type
                const production = job.job_production?.find(p => p.quote_type === quoteType);

                quotes[quoteType] = {
                    isRequired: quote.is_required,
                    designStatus: quote.design_status,
                    estimationStatus: quote.estimation_status,
                    estimationValue: quote.estimation_value,
                    estimationNotes: quote.estimation_notes,
                    expectedFinishDate: quote.expected_finish_date,
                    expectedDeliveryDate: quote.expected_delivery_date,
                    productionStatus: quote.production_status || 'not_started',
                    designers: designers,
                    production: production ? {
                        id: production.id,
                        userId: production.user_id,
                        name: production.name,
                        phase: quote.production_status || 'not_started', // Use production_status from job_quotes
                        isStartedCutting: production.is_started_cutting,
                        isStartedProduction: production.is_started_production,
                        isCompleted: production.is_completed,
                        startCuttingAt: production.start_cutting_at,
                        endCuttingAt: production.end_cutting_at,
                        startProductionAt: production.start_production_at,
                        endProductionAt: production.end_production_at,
                        productionNotes: production.production_notes
                    } : {
                        phase: quote.production_status || 'not_started' // If no production record, still provide phase from quote
                    }
                };
            });


            // Get estimator
            const estimator = job?.job_estimators ? {
                userId: job.job_estimators.user_id,
                name: job.job_estimators.name || job.job_estimators.estimator_user?.username || job.job_estimators.estimator_user?.full_name,
                assignedAt: job.job_estimators.assigned_at,
                startedAt: job.job_estimators.started_at,
                completedAt: job.job_estimators.completed_at,
                isStarted: job.job_estimators.is_started,
                isCompleted: job.job_estimators.is_completed,
                finishingNote: job.job_estimators.finishing_note
            } : null;

            return {
                _id: job.id,
                uniqueId: job.unique_id,
                customerName: job.customer_name,
                customerNumber: job.customer_number,
                customerMail: job.customer_mail,
                carpenterSupervisorName: job.carpenter_supervisor_name,
                carpenterSupervisorNumber: job.carpenter_supervisor_number,
                customerAddress: job.customer_address,
                jobType: job.job_type,
                mainStatus: job.main_status,
                currentStatus: job.current_status,
                invoiced: job.invoiced,
                bookingDeliveryDate: job.booking_delivery_date,
                expectedEstimationFinishDate: job.expected_estimation_finish_date,
                createdAt: job.created_at,
                updatedAt: job.updated_at,
                jobInformation: {
                    roofType: job.roof_type,
                    roofTrussCentres: job.roof_truss_centres,
                    isSingleStorey: job.is_single_storey,
                    isUnit: job.is_unit,
                    unitCount: job.unit_count,
                    urgentQuote: job.urgent_quote,
                    urgentDesign: job.urgent_design,
                    siteMeasureRequired: job.site_measure_required,
                    siteMeasureWall: job.site_measure_wall,
                    siteMeasureFloor: job.site_measure_floor,
                    siteMeasureTruss: job.site_measure_truss
                },
                quotes: quotes,
                estimator: estimator,
                timberJob: false, // Set based on your timber logic
                uid_number: job.uid_number,
                uid_prefix: job.uid_prefix
            };
        });

        // Apply custom sorting for priority if needed
        if (sortBy === 'priority' && transformedData.length > 0) {
            transformedData.sort((a, b) => {
                // Helper function to get earliest date
                const getEarliestDate = (job) => {
                    const dates = [];

                    // Add job-level booking_delivery_date if exists
                    if (job.bookingDeliveryDate) {
                        dates.push(new Date(job.bookingDeliveryDate));
                    }

                    // Add quote-level expected_finish_date and expected_delivery_date if they exist
                    if (job.quotes) {
                        Object.values(job.quotes).forEach(quote => {
                            if (quote.expectedFinishDate) {
                                dates.push(new Date(quote.expectedFinishDate));
                            }
                            if (quote.expectedDeliveryDate) {
                                dates.push(new Date(quote.expectedDeliveryDate));
                            }
                        });
                    }

                    // Return the earliest date, or null if no dates
                    return dates.length > 0 ? new Date(Math.min(...dates)) : null;
                };

                // Determine urgency levels
                const aIsUrgentQuote = a.jobInformation?.urgentQuote || false;
                const bIsUrgentQuote = b.jobInformation?.urgentQuote || false;
                const aIsUrgentDesign = a.jobInformation?.urgentDesign || false;
                const bIsUrgentDesign = b.jobInformation?.urgentDesign || false;

                // Both urgent quote: sort by date
                if (aIsUrgentQuote && bIsUrgentQuote) {
                    const dateA = getEarliestDate(a);
                    const dateB = getEarliestDate(b);
                    if (dateA === null && dateB === null) return 0;
                    if (dateA === null) return 1;
                    if (dateB === null) return -1;
                    return dateA.getTime() - dateB.getTime();
                }

                // One is urgent quote, other is not: urgent quote comes first
                if (aIsUrgentQuote !== bIsUrgentQuote) {
                    return bIsUrgentQuote ? 1 : -1;
                }

                // Both urgent design (but not urgent quote): sort by date
                if (aIsUrgentDesign && bIsUrgentDesign) {
                    const dateA = getEarliestDate(a);
                    const dateB = getEarliestDate(b);
                    if (dateA === null && dateB === null) return 0;
                    if (dateA === null) return 1;
                    if (dateB === null) return -1;
                    return dateA.getTime() - dateB.getTime();
                }

                // One is urgent design, other is not: urgent design comes first
                if (aIsUrgentDesign !== bIsUrgentDesign) {
                    return bIsUrgentDesign ? 1 : -1;
                }

                // Neither urgent: sort by date
                const dateA = getEarliestDate(a);
                const dateB = getEarliestDate(b);
                if (dateA === null && dateB === null) return 0;
                if (dateA === null) return 1;
                if (dateB === null) return -1;
                return dateA.getTime() - dateB.getTime();
            });
        }

        return {
            data: transformedData,
            pagination: {
                page,
                limit,
                total: count || 0,
                hasMore: count ? (page * limit) < count : false
            }
        };
    } catch (error) {
        console.error('Error fetching production table data:', error);
        throw error;
    }
};


/**
 * Fetch scheduled assignments organized by week and day
 */
export const getScheduleAssignments = async (companyId, quoteType, filters = {}) => {
    try {
        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(
                    id,
                    quote_type,
                    is_required,
                    design_status,
                    estimation_status,
                    expected_finish_date,
                    expected_delivery_date,
                    delivery_count,
                    production_status,
                    invoiced,
                    invoice_state,
                    on_hold,
                    layout_only,
                    waiting_job,
                    sent_production,
                    sort_order,
                    child_unique_id,
                    timber_is_checked,
                    quick_notes,
                    quote_playground,
                    delivery_playground,
                    delivery_time_slot,
                    delivery_tag,
                    xero_invoice_status,
                    xero_invoice_id,
                    xero_invoice_number,
                    xero_last_synced_at,
                    site_measure_confirmed,
                    site_measure_date
                ),
                schedule_quotes:job_quotes!inner(id),
                job_designers(
                    id,
                    quote_type,
                    child_job_id,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_estimators(
                    id,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_production(
                    id,
                    quote_type,
                    phase,
                    is_completed,
                    quote_id
                ),
                job_deliveries(
                    id,
                    quote_type,
                    delivery_status,
                    assigned_driver_name,
                    child_id
                )
            `)
            .eq('company_id', companyId)
            .neq('job_type', "Estimation")
            .eq('deleted', false);

        // We fetch ALL job_quotes (not just the specific quoteType) for status display
        // But we still filter which jobs to show based on the specific quote type
        // This is done in JavaScript after fetching
        query = applyCancelledFilter(query, filters);

        if (quoteType !== 'delivery') {
            const monday = filters.weekStart ? new Date(filters.weekStart) : new Date();

            monday.setHours(0, 0, 0, 0);

            const nextMonday = new Date(monday);
            nextMonday.setDate(monday.getDate() + 7);

            query = query
                .eq('schedule_quotes.quote_type', quoteType)
                .eq('schedule_quotes.is_required', true)
                .or(
                    'and(quote_playground.is.null,delivery_playground.is.null),' +
                    'and(quote_playground.is.null,delivery_playground.eq.false),' +
                    'and(quote_playground.eq.false,delivery_playground.is.null),' +
                    'and(quote_playground.eq.false,delivery_playground.eq.false)',
                    { foreignTable: 'schedule_quotes' }
                )
                .or('delivery_count.is.null,delivery_count.eq.0', {
                    foreignTable: 'schedule_quotes'
                })
                .or(
                    `and(expected_finish_date.gte.${monday.toISOString()},expected_finish_date.lt.${nextMonday.toISOString()}),` +
                    `and(expected_delivery_date.gte.${monday.toISOString()},expected_delivery_date.lt.${nextMonday.toISOString()})`,
                    { foreignTable: 'schedule_quotes' }
                );
        }

        // Get only scheduled assignments (has expected_finish_date)
        if (quoteType === 'delivery') {
            query = query.not('booking_delivery_date', 'is', null);
        } else {
            // query = query.not('job_quotes.expected_finish_date', 'is', null);
        }

        // Apply user filters
        if (filters.userId) {
            if (['roof', 'wall', 'floor', 'timber'].includes(quoteType)) {
                const { data: designerJobs } = await supabase
                    .from('job_designers')
                    .select('job_id')
                    .eq('user_id', filters.userId)
                    .eq('quote_type', quoteType);

                const jobIds = designerJobs?.map(d => d.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    return { data: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] }, error: null };
                }
            }
        } else if (filters.showOnlyMine) {
            if (['roof', 'wall', 'floor', 'timber'].includes(quoteType)) {
                const { data: designerJobs } = await supabase
                    .from('job_designers')
                    .select('job_id')
                    .eq('user_id', filters.showOnlyMine)
                    .eq('quote_type', quoteType);

                const jobIds = designerJobs?.map(d => d.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    return { data: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] }, error: null };
                }
            }
        }

        const { data, error } = await query;

        if (error) throw error;

        // Filter job_quotes to only include initial quotes (delivery_count is null, undefined, or 0)
        // Keep ALL quotes for status display, but filter jobs to only show those with the specific quote type
        const filteredData = data?.map(job => ({
            ...job,
            job_quotes: job.job_quotes?.filter(quote =>
                (
                    quote.delivery_count === null ||
                    quote.delivery_count === undefined ||
                    quote.delivery_count === 0
                ) &&
                quote.quote_playground !== true &&
                quote.delivery_playground !== true
            )
        })).filter(job => {
            // For delivery schedule, show all jobs with quotes
            if (quoteType === 'delivery') {
                return job.job_quotes && job.job_quotes.length > 0;
            }
            // For quote schedules (roof, wall, floor, timber), only show jobs that have
            // at least one required + scheduled quote of this type
            const scheduledQuotes = job.job_quotes?.filter(q =>
                q.quote_type === quoteType && q.is_required && (q.expected_delivery_date || q.expected_finish_date)
            );
            if (!scheduledQuotes || scheduledQuotes.length === 0) return false;

            // Apply design status filter if provided (keep job if ANY matching quote passes)
            if (filters.designStatusFilter) {
                return scheduledQuotes.some(q => {
                    if (filters.designStatusFilter === "waiting_assignment") {
                        return (
                            q.design_status === "waiting_assignment" ||
                            q.design_status === "not_required"
                        );
                    }

                    return q.design_status === filters.designStatusFilter;
                });
            }
            return true;
        });


        // Transform and organize by week and day
        const organized = {
            Monday: [],
            Tuesday: [],
            Wednesday: [],
            Thursday: [],
            Friday: [],
            Saturday: []
        };

        // Use the provided weekStart or default to current week
        let monday;
        if (filters.weekStart) {
            monday = new Date(filters.weekStart);
        } else {
            const today = new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
            monday = new Date(today);
            monday.setDate(today.getDate() - today.getDay() + 1);
        }
        // Normalize to start of day in UTC
        monday.setUTCHours(0, 0, 0, 0);

        filteredData?.forEach(job => {
            // ── Delivery (one row per job, unchanged) ────────────────────────
            if (quoteType === 'delivery') {
                const dateToUse = job.booking_delivery_date;
                if (!dateToUse) return;
                const quote = { quote_type: 'delivery' };
                const delivery = job.job_deliveries?.find(d => d.quote_type === 'delivery') || null;
                const transformedJob = {
                    ...job,
                    schedule_row_id: job.unique_id,
                    quote_info: {
                        ...quote,
                        design_status: job.is_cancelled ? 'cancelled' : quote.design_status,
                    },
                    designers: [],
                    production: null,
                    delivery
                };
                let jobDate;
                if (typeof dateToUse === 'string') {
                    const p = dateToUse.split('T')[0].split('-');
                    jobDate = new Date(Date.UTC(parseInt(p[0]), parseInt(p[1]) - 1, parseInt(p[2]), 0, 0, 0, 0));
                } else {
                    jobDate = new Date(dateToUse);
                    jobDate.setUTCHours(0, 0, 0, 0);
                }
                const diffDays = Math.floor((jobDate.getTime() - monday.getTime()) / (1000 * 60 * 60 * 24));
                if (diffDays >= 0 && diffDays <= 7) {
                    const dayOfWeek = jobDate.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
                    if (organized[dayOfWeek]) organized[dayOfWeek].push(transformedJob);
                }
                return;
            }

            // ── Non-delivery: one row per scheduled quote of this type ────────
            // Pre-count required quotes per type for the 3-case designer filter
            const requiredCountByType = {};
            job.job_quotes?.forEach(q => {
                if (q.is_required && !(q.delivery_count > 0)) {
                    requiredCountByType[q.quote_type] = (requiredCountByType[q.quote_type] || 0) + 1;
                }
            });
            const isMultiType = (requiredCountByType[quoteType] || 0) > 1;

            const quotesOfType = job.job_quotes?.filter(q =>
                q.quote_type === quoteType &&
                q.is_required &&
                (q.expected_delivery_date || q.expected_finish_date) &&
                !(q.delivery_count > 0)
            ) || [];

            quotesOfType.forEach(quote => {
                // Apply per-quote design status filter when set
                if (filters.designStatusFilter) {
                    const passesDesignStatus =
                        filters.designStatusFilter === "waiting_assignment"
                            ? (
                                quote.design_status === "waiting_assignment" ||
                                quote.design_status === "not_required"
                            )
                            : quote.design_status === filters.designStatusFilter;

                    if (!passesDesignStatus) return;
                }

                const dateToUse = quote.expected_delivery_date || quote.expected_finish_date;
                if (!dateToUse) return;

                // 3-case designer filter: child_job_id match → type fallback (single) → skip
                const designers = job.job_designers?.filter(d => {
                    if (d.child_job_id) return d.child_job_id === quote.id;
                    if (!isMultiType) return d.quote_type === quoteType;
                    return false;
                }) || [];

                const production = job.job_production?.find(p => p.quote_type === quoteType);
                const delivery = job.job_deliveries?.find(d => d.quote_type === quoteType);

                const transformedJob = {
                    ...job,
                    schedule_row_id: quote.child_unique_id || job.unique_id,
                    schedule_row_uuid: quote.id || job?.id,
                    quote_info: {
                        ...quote,
                        design_status: job.is_cancelled ? 'cancelled' : quote.design_status,
                    },
                    designers,
                    production,
                    delivery
                };

                // Parse the date string as UTC to avoid timezone shifts
                let jobDate;
                if (typeof dateToUse === 'string') {
                    const p = dateToUse.split('T')[0].split('-');
                    jobDate = new Date(Date.UTC(parseInt(p[0]), parseInt(p[1]) - 1, parseInt(p[2]), 0, 0, 0, 0));
                } else {
                    jobDate = new Date(dateToUse);
                    jobDate.setUTCHours(0, 0, 0, 0);
                }

                const diffDays = Math.floor((jobDate.getTime() - monday.getTime()) / (1000 * 60 * 60 * 24));
                if (diffDays >= 0 && diffDays <= 7) {
                    const dayOfWeek = jobDate.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
                    if (organized[dayOfWeek]) organized[dayOfWeek].push(transformedJob);
                }
            });
        });

        // Sort each day by sort_order (nulls last)
        for (const day of Object.keys(organized)) {
            organized[day].sort((a, b) => {
                const aOrder = a.quote_info?.sort_order ?? null;
                const bOrder = b.quote_info?.sort_order ?? null;
                if (aOrder === null && bOrder === null) return 0;
                if (aOrder === null) return 1;
                if (bOrder === null) return -1;
                return aOrder - bOrder;
            });
        }

        return { data: organized, error: null };
    } catch (error) {
        console.error('Error fetching scheduled assignments:', error);
        return { data: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] }, error };
    }
};



/**
 * Fetch unscheduled assignments for a specific quote type
 */
export const getUnscheduledByQuote = async (companyId, quoteType, filters = {}) => {
    try {
        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(
                    id,
                    quote_type,
                    is_required,
                    design_status,
                    estimation_status,
                    expected_finish_date,
                    expected_delivery_date,
                    delivery_count,
                    production_status,
                    invoiced,
                    invoice_state,
                    on_hold,
                    layout_only,
                    waiting_job,
                    sent_production,
                    sort_order,
                    child_unique_id,
                    timber_is_checked,
                    quick_notes,
                    quote_playground,
                    delivery_playground,
                    xero_invoice_status,
                    xero_invoice_id,
                    xero_invoice_number,
                    xero_last_synced_at,
                    site_measure_confirmed,
                    site_measure_date
                ),
                job_designers(
                    id,
                    quote_type,
                    child_job_id,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_estimators(
                    id,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_production(
                    id,
                    quote_type,
                    phase,
                    is_completed
                ),
                job_deliveries(
                    id,
                    quote_type,
                    delivery_status,
                    assigned_driver_name
                )
            `)
            .eq('company_id', companyId)
            .neq('job_type', "Estimation")
            .eq('deleted', false);


        // We fetch ALL job_quotes (not just the specific quoteType) for status display
        // Filtering is done in JavaScript after fetching
        query = applyCancelledFilter(query, filters);

        // Apply user filters
        if (filters.userId) {
            if (['roof', 'wall', 'floor', 'timber'].includes(quoteType)) {
                const { data: designerJobs } = await supabase
                    .from('job_designers')
                    .select('job_id')
                    .eq('user_id', filters.userId)
                    .eq('quote_type', quoteType);

                const jobIds = designerJobs?.map(d => d.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    return { data: [], error: null };
                }
            }
        } else if (filters.showOnlyMine) {
            if (['roof', 'wall', 'floor', 'timber'].includes(quoteType)) {
                const { data: designerJobs } = await supabase
                    .from('job_designers')
                    .select('job_id')
                    .eq('user_id', filters.showOnlyMine)
                    .eq('quote_type', quoteType);

                const jobIds = designerJobs?.map(d => d.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    return { data: [], error: null };
                }
            }
        }

        const { data, error } = await query;

        if (error) throw error;

        // Filter job_quotes to only include initial quotes (delivery_count is null or 0)
        const filteredData = (data || []).map(job => ({
            ...job,
            job_quotes: job.job_quotes?.filter(q =>
                (
                    q.delivery_count === null ||
                    q.delivery_count === undefined ||
                    q.delivery_count === 0
                ) &&
                q.quote_playground !== true &&
                q.delivery_playground !== true
            )
        }));

        // Build one row per unscheduled quote of this type
        let processedData = [];

        filteredData.forEach(job => {

            // DELIVERY SCHEDULE (unchanged)
            if (quoteType === 'delivery') {
                if (job.main_status === 'ready_for_delivery' && !job.booking_delivery_date) {
                    processedData.push({
                        ...job,
                        schedule_row_id: job.unique_id,
                        quote_info: { quote_type: 'delivery' },
                        designers: [],
                        production: null,
                        delivery: job.job_deliveries?.[0] || null
                    });
                }
                return;
            }

            // Get ALL unscheduled required quotes of this type
            const quotesOfType = job.job_quotes?.filter(q =>
                q.quote_type === quoteType &&
                q.is_required &&
                !(q.expected_delivery_date || q.expected_finish_date)
            ) || [];

            if (quotesOfType.length === 0) return;

            // Pre-count required quotes per type for the 3-case designer filter
            const requiredCountByType = {};
            job.job_quotes?.forEach(q => {
                if (q.is_required) {
                    requiredCountByType[q.quote_type] = (requiredCountByType[q.quote_type] || 0) + 1;
                }
            });
            const isMultiType = (requiredCountByType[quoteType] || 0) > 1;

            quotesOfType.forEach(quote => {

                if (quote?.delivery_count > 0) return;

                // Apply optional status filter
                if (filters.designStatusFilter &&
                    quote.design_status !== filters.designStatusFilter) {
                    return;
                }

                // 3-case designer filter: child_job_id match → type fallback (single) → skip
                const designers = job.job_designers?.filter(d => {
                    if (d.child_job_id) return d.child_job_id === quote.id;
                    if (!isMultiType) return d.quote_type === quoteType;
                    return false;
                }) || [];

                const production = job.job_production?.find(p =>
                    p.quote_type === quoteType
                );

                const delivery = job.job_deliveries?.find(d =>
                    d.quote_type === quoteType &&
                    d.delivery_count === quote.delivery_count
                );

                processedData.push({
                    ...job,
                    schedule_row_id: quote.child_unique_id || job?.unique_id,
                    schedule_row_uuid: quote.id || job?.id,
                    quote_info: {
                        ...quote,
                        design_status: job.is_cancelled ? 'cancelled' : quote.design_status,
                    },
                    designers,
                    production,
                    delivery
                });
            });

        });


        // Sort by sort_order (nulls last)
        processedData.sort((a, b) => {
            const aOrder = a.quote_info?.sort_order ?? null;
            const bOrder = b.quote_info?.sort_order ?? null;
            if (aOrder === null && bOrder === null) return 0;
            if (aOrder === null) return 1;
            if (bOrder === null) return -1;
            return aOrder - bOrder;
        });

        return { data: processedData, error: null };
    } catch (error) {
        console.error('Error fetching unscheduled assignments:', error);
        return { data: [], error };
    }
};


/**
 * Fetch on-hold jobs for a given company.
 * For department pages (roof, wall, floor, timber) it returns jobs where the specific
 * quote's on_hold flag is true.
 * For the delivery page it returns jobs where any job_quote has on_hold = true.
 *
 * @param {string} companyId
 * @param {string} quoteType  - quote type key, e.g. 'roof' | 'wall' | 'floor' | 'timber' | 'delivery'
 * @param {object} filters    - optional: { userId, showOnlyMine }
 */
export const getOnHoldJobs = async (companyId, quoteType, filters = {}) => {
    try {
        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(
                    id,
                    quote_type,
                    is_required,
                    design_status,
                    estimation_status,
                    expected_finish_date,
                    expected_delivery_date,
                    delivery_count,
                    production_status,
                    invoiced,
                    on_hold,
                     xero_invoice_status,
                    xero_invoice_id,
                    xero_invoice_number,
                    xero_last_synced_at,
                     site_measure_confirmed,
                    site_measure_date
                ),
                job_designers(
                    id,
                    quote_type,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_estimators(
                    id,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_production(
                    id,
                    quote_type,
                    phase,
                    is_completed
                ),
                job_deliveries(
                    id,
                    quote_type,
                    delivery_status
                )
            `)
            .eq('company_id', companyId)
            .eq('deleted', false);

        // Apply user filter for department pages
        if (filters.userId && ['roof', 'wall', 'floor', 'timber'].includes(quoteType)) {
            const { data: designerJobs } = await supabase
                .from('job_designers')
                .select('job_id')
                .eq('user_id', filters.userId)
                .eq('quote_type', quoteType);
            const jobIds = designerJobs?.map(d => d.job_id) || [];
            if (jobIds.length === 0) return { data: [], error: null };
            query = query.in('id', jobIds);
        } else if (filters.showOnlyMine && ['roof', 'wall', 'floor', 'timber'].includes(quoteType)) {
            const { data: designerJobs } = await supabase
                .from('job_designers')
                .select('job_id')
                .eq('user_id', filters.showOnlyMine)
                .eq('quote_type', quoteType);
            const jobIds = designerJobs?.map(d => d.job_id) || [];
            if (jobIds.length === 0) return { data: [], error: null };
            query = query.in('id', jobIds);
        }

        const { data, error } = await query;
        if (error) throw error;

        // Filter to only initial quotes (delivery_count null or 0)
        const withInitialQuotes = data?.map(job => ({
            ...job,
            job_quotes: job.job_quotes?.filter(q =>
                q.delivery_count === null || q.delivery_count === undefined || q.delivery_count === 0
            ),
        })) || [];

        // Keep only jobs where the relevant quote is on hold
        const onHoldJobs = withInitialQuotes.filter(job => {
            if (quoteType === 'delivery') {
                // For delivery: any required quote is on hold
                return job.job_quotes?.some(q => q.is_required && q.on_hold === true);
            }
            const specificQuote = job.job_quotes?.find(q => q.quote_type === quoteType);
            return specificQuote && specificQuote.is_required && specificQuote.on_hold === true;
        });

        // Transform to same shape as getUnscheduledByQuote
        const processedData = onHoldJobs.map(job => {
            if (quoteType === 'delivery') {
                const quote = job.job_quotes?.find(q => q.is_required && q.on_hold === true);
                return {
                    ...job,
                    quote_info: quote || { quote_type: 'delivery' },
                    designers: [],
                    production: null,
                    delivery: job.job_deliveries?.[0] || null,
                };
            }
            const quote = job.job_quotes?.find(q => q.quote_type === quoteType);
            const designers = job.job_designers?.filter(d => d.quote_type === quoteType) || [];
            const production = job.job_production?.find(p => p.quote_type === quoteType);
            const delivery = job.job_deliveries?.find(d => d.quote_type === quoteType);
            return {
                ...job,
                quote_info: quote,
                designers,
                production,
                delivery,
            };
        });

        return { data: processedData, error: null };
    } catch (error) {
        console.error('Error fetching on-hold jobs:', error);
        return { data: [], error };
    }
};

/**
 * Fetch all jobs whose quote (for the given quoteType) has waiting_job = true.
 * Returns data in the same shape as getOnHoldJobs / getUnscheduledByQuote.
 */
export const getWaitingJobs = async (companyId, quoteType, filters = {}) => {
    try {
        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(
                    id,
                    quote_type,
                    is_required,
                    design_status,
                    estimation_status,
                    expected_finish_date,
                    expected_delivery_date,
                    delivery_count,
                    production_status,
                    invoiced,
                    on_hold,
                    waiting_job,
                     xero_invoice_status,
                    xero_invoice_id,
                    xero_invoice_number,
                    xero_last_synced_at,
                     site_measure_confirmed,
                    site_measure_date
                ),
                job_designers(
                    id,
                    quote_type,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_estimators(
                    id,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_production(
                    id,
                    quote_type,
                    phase,
                    is_completed
                ),
                job_deliveries(
                    id,
                    quote_type,
                    delivery_status
                )
            `)
            .eq('company_id', companyId)
            .eq('deleted', false);

        if (filters.userId && ['roof', 'wall', 'floor', 'timber'].includes(quoteType)) {
            const { data: designerJobs } = await supabase
                .from('job_designers')
                .select('job_id')
                .eq('user_id', filters.userId)
                .eq('quote_type', quoteType);
            const jobIds = designerJobs?.map(d => d.job_id) || [];
            if (jobIds.length === 0) return { data: [], error: null };
            query = query.in('id', jobIds);
        } else if (filters.showOnlyMine && ['roof', 'wall', 'floor', 'timber'].includes(quoteType)) {
            const { data: designerJobs } = await supabase
                .from('job_designers')
                .select('job_id')
                .eq('user_id', filters.showOnlyMine)
                .eq('quote_type', quoteType);
            const jobIds = designerJobs?.map(d => d.job_id) || [];
            if (jobIds.length === 0) return { data: [], error: null };
            query = query.in('id', jobIds);
        }

        const { data, error } = await query;
        if (error) throw error;

        // Filter to only initial quotes (delivery_count null or 0)
        const withInitialQuotes = data?.map(job => ({
            ...job,
            job_quotes: job.job_quotes?.filter(q =>
                q.delivery_count === null || q.delivery_count === undefined || q.delivery_count === 0
            ),
        })) || [];

        // Keep only jobs where the relevant quote has waiting_job = true
        const waitingJobs = withInitialQuotes.filter(job => {
            const specificQuote = job.job_quotes?.find(q => q.quote_type === quoteType);
            return specificQuote && specificQuote.is_required && specificQuote.waiting_job === true;
        });

        // Transform to same shape as getUnscheduledByQuote
        const processedData = waitingJobs.map(job => {
            const quote = job.job_quotes?.find(q => q.quote_type === quoteType);
            const designers = job.job_designers?.filter(d => d.quote_type === quoteType) || [];
            const production = job.job_production?.find(p => p.quote_type === quoteType);
            const delivery = job.job_deliveries?.find(d => d.quote_type === quoteType);
            return {
                ...job,
                quote_info: quote,
                designers,
                production,
                delivery,
            };
        });

        return { data: processedData, error: null };
    } catch (error) {
        console.error('Error fetching waiting jobs:', error);
        return { data: [], error };
    }
};

/**
 * Update job quote expected finish date or delivery booking date
 * @param {string} jobId - The job ID
 * @param {string} quoteType - The quote type (roof, wall, floor, delivery)
 * @param {Date|null} newDate - The new date or null to unschedule
 * @returns {Promise<Object>} Result of the update
 */
export const updateFinishDate = async (jobId, quoteType, newDate, quoteId = null) => {
    try {
        const updateData = {
            updated_at: new Date().toISOString()
        };

        let data, error;

        if (quoteType === 'delivery') {
            // Update the jobs table booking_delivery_date for delivery
            updateData.booking_delivery_date = newDate ? newDate.toISOString() : null;

            const result = await supabase
                .from('jobs')
                .update(updateData)
                .eq('id', jobId)
                .select()
                .single();

            data = result.data;
            error = result.error;
        } else {
            // Update the job_quotes table — always target by primary key id
            updateData.expected_finish_date = newDate ? newDate.toISOString() : null;
            updateData.expected_delivery_date = newDate ? newDate.toISOString() : null;

            let q = supabase.from('job_quotes').update(updateData);
            if (quoteId) {
                q = q.eq('id', quoteId);
            } else {
                // Fallback for edge cases: match by job_id + quote_type
                q = q.eq('job_id', jobId).eq('quote_type', quoteType);
            }

            const result = await q.select();

            data = result.data;
            error = result.error;
        }

        if (error) throw error;

        return { data, error: null };
    } catch (error) {
        console.error('Error updating finish date:', error);
        return { data: null, error };
    }
};


/**
 * Batch update sort_order for an ordered list of job_quotes.
 * @param {Array<{jobId: string, quoteType: string, sortOrder: number, scheduleRowId?: string}>} updates
 *   scheduleRowId (= child_unique_id) is preferred when available for precise targeting.
 */
export const batchUpdateSortOrder = async (updates) => {
    try {
        await Promise.all(
            updates.map(({ jobId, quoteType, sortOrder, scheduleRowId }) => {
                let q = supabase
                    .from('job_quotes')
                    .update({ sort_order: sortOrder, updated_at: new Date().toISOString() });
                if (scheduleRowId) {
                    q = q.eq('child_unique_id', scheduleRowId);
                } else {
                    q = q.eq('job_id', jobId)
                        .eq('quote_type', quoteType)
                        .or('delivery_count.is.null,delivery_count.eq.0');
                }
                return q;
            })
        );
        return { error: null };
    } catch (error) {
        console.error('Error batch updating sort order:', error);
        return { error };
    }
};

/**
 * Reset sort_order to null for a specific job_quote (used when moving cross-day).
 * @param {string} jobId
 * @param {string} quoteType
 * @param {string} [scheduleRowId] - child_unique_id of the specific quote row (preferred)
 */
export const resetSortOrder = async (jobId, quoteType, quoteId = null) => {
    try {
        let q = supabase
            .from('job_quotes')
            .update({ sort_order: null, updated_at: new Date().toISOString() });
        if (quoteId) {
            q = q.eq('id', quoteId);
        } else {
            q = q.eq('job_id', jobId).eq('quote_type', quoteType);
        }
        const { error } = await q;
        return { error: error || null };
    } catch (error) {
        console.error('Error resetting sort order:', error);
        return { error };
    }
};

/**
 * Fetch all assignments that are pinned to the quote playground for a given type.
 * Ignores scheduling status — returns cards regardless of which week they are on.
 */
export const getQuotePlaygroundAssignments = async (companyId, quoteType) => {
    try {
        // Step 1: get all job_quotes rows flagged for playground
        const { data: pgQuotes, error: pgError } = await supabase
            .from('job_quotes')
            .select('id, job_id')
            .eq('quote_type', quoteType)
            .or('quote_playground.eq.true,delivery_playground.eq.true');

        if (pgError) throw pgError;
        if (!pgQuotes?.length) return { data: [], error: null };

        const pgQuoteIds = new Set(pgQuotes.map(q => q.id));
        const jobIds = [...new Set(pgQuotes.map(q => q.job_id))];

        // Step 2: fetch full job data for those jobs
        const { data, error } = await supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(
                    id,
                    quote_type,
                    is_required,
                    design_status,
                    estimation_status,
                    expected_finish_date,
                    expected_delivery_date,
                    delivery_count,
                    production_status,
                    invoiced,
                    invoice_state,
                    on_hold,
                    layout_only,
                    waiting_job,
                    sent_production,
                    sort_order,
                    child_unique_id,
                    timber_is_checked,
                    quote_playground,
                    delivery_playground,
                    quick_notes,
                     xero_invoice_status,
                    xero_invoice_id,
                    xero_invoice_number,
                    xero_last_synced_at,
                     site_measure_confirmed,
                    site_measure_date
                ),
                job_designers(
                    id,
                    quote_type,
                    child_job_id,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_estimators(
                    id,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_production(
                    id,
                    quote_type,
                    phase,
                    is_completed
                ),
                job_deliveries(
                    id,
                    quote_type,
                    delivery_status,
                    assigned_driver_name
                )
            `)
            .in('id', jobIds)
            .eq('company_id', companyId)
            .eq('deleted', false);

        if (error) throw error;

        const processedData = [];

        (data || []).forEach(job => {
            const requiredCountByType = {};
            job.job_quotes?.forEach(q => {
                if (q.is_required) {
                    requiredCountByType[q.quote_type] = (requiredCountByType[q.quote_type] || 0) + 1;
                }
            });
            const isMultiType = (requiredCountByType[quoteType] || 0) > 1;

            job.job_quotes?.filter(q => pgQuoteIds.has(q.id)).forEach(quote => {
                const designers = job.job_designers?.filter(d => {
                    if (d.child_job_id) return d.child_job_id === quote.id;
                    if (!isMultiType) return d.quote_type === quoteType;
                    return false;
                }) || [];

                const production = job.job_production?.find(p => p.quote_type === quoteType);
                const delivery = job.job_deliveries?.find(d =>
                    d.quote_type === quoteType && d.delivery_count === quote.delivery_count
                );

                processedData.push({
                    ...job,
                    schedule_row_id: quote.child_unique_id || job?.unique_id,
                    schedule_row_uuid: quote.id || job?.id,
                    quote_info: quote,
                    designers,
                    production,
                    delivery,
                });
            });
        });

        return { data: processedData, error: null };
    } catch (error) {
        console.error('Error fetching quote playground assignments:', error);
        return { data: [], error };
    }
};

/**
 * Toggle both playground flags on a specific job_quotes row.
 * @param {string} quoteId - job_quotes.id (UUID)
 * @param {boolean} value
 */
export const setQuotePlayground = async (quoteId, value) => {
    const update = {
        quote_playground: value,
        delivery_playground: value,
    };

    if (value === true) {
        update.expected_finish_date = null;
        update.expected_delivery_date = null;
    }

    const { error } = await supabase
        .from('job_quotes')
        .update(update)
        .eq('id', quoteId);

    if (error) throw error;
};
/**
 * Toggle the delivery_playground flag on a specific job_quotes row.
 * @param {string} quoteId - job_quotes.id (UUID)
 * @param {boolean} value
 */
export const setDeliveryPlayground = async (quoteId, value) => {
    const update = {
        delivery_playground: value,
        quote_playground: value,
    };

    if (value === true) {
        update.expected_delivery_date = null;
        update.expected_finish_date = null;
    }
    const { error } = await supabase
        .from('job_quotes')
        .update(update)
        .eq('id', quoteId);
    if (error) throw error;
};


/**
 * Fetch scheduled delivery assignments organized by week and day
 * NOW RETURNS QUOTE-BASED CARDS: If a job has 2 required quotes, it will appear as 2 separate cards
 * @param {string} companyId - The company ID
 * @param {Object} filters - Filter options (userId, showOnlyMine)
 * @returns {Promise<Object>} Organized delivery data by week and day (one card per quote)
 */
export const getScheduledDeliveries = async (companyId, filters = {}) => {
    try {
        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(
                    id,
                    quote_type,
                    is_required,
                    design_status,
                    estimation_status,
                    expected_delivery_date,
                    invoiced,
                     xero_invoice_status,
                    xero_invoice_id,
                    xero_invoice_number,
                    xero_last_synced_at
                ),
                job_designers(
                    id,
                    quote_type,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_estimators(
                    id,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_production(
                    id,
                    quote_type,
                    phase,
                    is_completed
                ),
                job_deliveries(
                    id,
                    quote_type,
                    delivery_status,
                    assigned_driver_id,
                    assigned_driver_name
                )
            `)
            .eq('company_id', companyId)
            .eq('deleted', false);
        // .in('main_status', ['ready_for_delivery', 'delivery_scheduled', 'in_delivery']);

        // Apply user filters for delivery (filter by assigned driver)
        if (filters.userId) {
            const { data: deliveryJobs } = await supabase
                .from('job_deliveries')
                .select('job_id')
                .eq('assigned_driver_id', filters.userId);

            const jobIds = deliveryJobs?.map(d => d.job_id) || [];
            if (jobIds.length > 0) {
                query = query.in('id', jobIds);
            } else {
                return {
                    data: {
                        week1: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week2: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week3: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week4: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] }
                    },
                    error: null
                };
            }
        } else if (filters.showOnlyMine) {
            const { data: deliveryJobs } = await supabase
                .from('job_deliveries')
                .select('job_id')
                .eq('assigned_driver_id', filters.showOnlyMine);

            const jobIds = deliveryJobs?.map(d => d.job_id) || [];
            if (jobIds.length > 0) {
                query = query.in('id', jobIds);
            } else {
                return {
                    data: {
                        week1: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week2: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week3: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week4: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] }
                    },
                    error: null
                };
            }
        }

        const { data, error } = await query;


        if (error) throw error;

        // Organize by week and day
        const organized = {
            week1: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
            week2: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
            week3: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
            week4: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
        };

        // IMPORTANT: baseMonday is ALWAYS the current week Monday (week1)
        // This ensures week numbers are consistent: week1=current, week2=next, etc.
        const today = new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
        const baseMonday = new Date(today);
        baseMonday.setDate(today.getDate() - today.getDay() + 1);
        baseMonday.setHours(0, 0, 0, 0);

        // Calculate the view window (which 4 weeks to show)
        let viewWindowStart;
        if (filters.weekStart) {
            const receivedDate = new Date(filters.weekStart);
            const melbourneDate = new Date(receivedDate.toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
            viewWindowStart = new Date(melbourneDate);
            viewWindowStart.setHours(0, 0, 0, 0);
        } else {
            viewWindowStart = new Date(baseMonday);
        }

        // NEW LOGIC: Create one card per quote instead of per job
        data?.forEach(job => {
            const requiredQuotes = job.job_quotes?.filter(q => q.is_required) || [];

            requiredQuotes.forEach(quote => {
                // Show if quote has expected_delivery_date (driver is optional now)
                if (!quote.expected_delivery_date) {
                    return; // Skip this quote if it doesn't have a delivery date
                }


                const transformedQuoteCard = {
                    ...job,
                    quote_info: {
                        quote_type: quote.quote_type,
                        expected_finish_date: quote.expected_delivery_date,
                        design_status: quote.design_status,
                        estimation_status: quote.estimation_status,
                        invoiced: quote.invoiced,
                        id: quote.id
                    },
                    designers: job.job_designers?.filter(d => d.quote_type === quote.quote_type) || [],
                    production: job.job_production?.find(p => p.quote_type === quote.quote_type) || null,
                    delivery: job.job_deliveries?.find(d => d.quote_type === quote.quote_type) || null
                };

                // Parse date and normalize to start of day in Melbourne timezone for consistent comparison
                const quoteDateRaw = new Date(quote.expected_delivery_date);
                const quoteDateMelbourne = new Date(quoteDateRaw.toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
                const quoteDate = new Date(quoteDateMelbourne);
                quoteDate.setHours(0, 0, 0, 0);

                // Calculate days difference from baseMonday
                const diffDays = Math.floor((quoteDate - baseMonday) / (1000 * 60 * 60 * 24));

                // Only include assignments within the 4-week window (28 days) starting from baseMonday
                if (diffDays >= 0 && diffDays < 28) {
                    const weekNum = Math.floor(diffDays / 7) + 1;
                    const dayOfWeek = quoteDate.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'Australia/Melbourne' });

                    if (organized[`week${weekNum}`] && organized[`week${weekNum}`][dayOfWeek]) {
                        organized[`week${weekNum}`][dayOfWeek].push(transformedQuoteCard);
                    }
                }
            });
        });

        return { data: organized, error: null };
    } catch (error) {
        console.error('Error fetching scheduled deliveries:', error);
        return {
            data: {
                week1: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                week2: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                week3: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                week4: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] }
            },
            error
        };
    }
};

/**
 * Fetch unscheduled delivery assignments
 * NOW RETURNS QUOTE-BASED CARDS: Creates one card per required quote
 * A quote appears in unscheduled if it's missing EITHER expected_delivery_date OR assigned driver
 * @param {string} companyId - The company ID
 * @param {Object} filters - Filter options (userId, showOnlyMine)
 * @returns {Promise<Object>} Unscheduled delivery data (one card per quote)
 */
export const getUnscheduledDeliveries = async (companyId, filters = {}) => {
    try {
        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(
                    id,
                    quote_type,
                    is_required,
                    design_status,
                    estimation_status,
                    expected_delivery_date,
                    invoiced,
                     xero_invoice_status,
                    xero_invoice_id,
                    xero_invoice_number,
                    xero_last_synced_at
                ),
                job_designers(
                    id,
                    quote_type,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_estimators(
                    id,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_production(
                    id,
                    quote_type,
                    phase,
                    is_completed
                ),
                job_deliveries(
                    id,
                    quote_type,
                    delivery_status,
                    assigned_driver_id,
                    assigned_driver_name
                )
            `)
            .eq('company_id', companyId)
            .eq('deleted', false);
        // .in('main_status', ['ready_for_delivery', 'delivery_scheduled', 'in_delivery']);

        // Apply user filters for delivery (filter by assigned driver)
        if (filters.userId) {
            const { data: deliveryJobs } = await supabase
                .from('job_deliveries')
                .select('job_id')
                .eq('assigned_driver_id', filters.userId);

            const jobIds = deliveryJobs?.map(d => d.job_id) || [];
            if (jobIds.length > 0) {
                query = query.in('id', jobIds);
            } else {
                return { data: [], error: null };
            }
        } else if (filters.showOnlyMine) {
            const { data: deliveryJobs } = await supabase
                .from('job_deliveries')
                .select('job_id')
                .eq('assigned_driver_id', filters.showOnlyMine);

            const jobIds = deliveryJobs?.map(d => d.job_id) || [];
            if (jobIds.length > 0) {
                query = query.in('id', jobIds);
            } else {
                return { data: [], error: null };
            }
        }

        const { data, error } = await query;

        if (error) throw error;

        // NEW LOGIC: Create one card per quote, but only for quotes missing delivery date
        const processedData = [];
        data?.forEach(job => {
            const requiredQuotes = job.job_quotes?.filter(q => q.is_required) || [];

            requiredQuotes.forEach(quote => {
                const delivery = job.job_deliveries?.find(d => d.quote_type === quote.quote_type);

                // Show in unscheduled if missing delivery date (driver is optional)
                if (!quote.expected_delivery_date) {
                    const transformedQuoteCard = {
                        ...job,
                        quote_info: {
                            quote_type: quote.quote_type,
                            expected_finish_date: quote.expected_delivery_date || null,
                            design_status: quote.design_status,
                            estimation_status: quote.estimation_status,
                            invoiced: quote.invoiced,
                            id: quote.id
                        },
                        designers: job.job_designers?.filter(d => d.quote_type === quote.quote_type) || [],
                        production: job.job_production?.find(p => p.quote_type === quote.quote_type) || null,
                        delivery: delivery || null
                    };

                    processedData.push(transformedQuoteCard);
                }
            });
        });

        return { data: processedData, error: null };
    } catch (error) {
        console.error('Error fetching unscheduled deliveries:', error);
        return { data: [], error };
    }
};

/**
 * Update delivery expected date for a specific quote
 * NOW UPDATES: job_quotes.expected_delivery_date instead of jobs.booking_delivery_date
 * @param {string} jobId - The job ID
 * @param {string} quoteType - The quote type (roof, wall, floor, timber)
 * @param {Date|null} newDate - The new date or null to unschedule
 * @returns {Promise<Object>} Result of the update
 */
export const updateDeliveryDate = async (jobId, quoteType, newDate) => {
    try {
        const updateData = {
            expected_delivery_date: newDate ? newDate.toISOString() : null,
            updated_at: new Date().toISOString()
        };

        const { data, error } = await supabase
            .from('job_quotes')
            .update(updateData)
            .eq('job_id', jobId)
            .eq('quote_type', quoteType)
            .select()
            .single();

        if (error) throw error;

        return { data, error: null };
    } catch (error) {
        console.error('Error updating delivery date:', error);
        return { data: null, error };
    }
};

// ──────────────────────────────────────────────────────────────────────────────
// Site Measure Schedule Functions
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Shared base query for site measure jobs.
 * Returns the Supabase query builder (awaited by callers).
 */
const siteMeasureBaseQuery = (companyId) =>
    supabase
        .from('jobs')
        .select(`
            *,
            job_quotes(
                id, quote_type, is_required, design_status, estimation_status,
                production_status, invoiced, invoice_state, on_hold,
                site_measure_date, site_measure_confirmed, delivery_count, child_unique_id,
                 xero_invoice_status,
                    xero_invoice_id,
                    xero_invoice_number,
                    xero_last_synced_at,
                    quick_notes
            ),
            job_designers(id, quote_type, child_job_id, user_id, name, is_started, is_completed),
            job_production(id, quote_type, phase, is_completed),
            job_deliveries(id, quote_type, delivery_status, assigned_driver_name)
        `)
        .eq('company_id', companyId)
        .eq('deleted', false)
        .or('is_cancelled.is.null,is_cancelled.eq.false')
        .eq('site_measure_required', true);

// Maps quote_type → job-level site_measure flag name
const SM_TYPE_FLAG = {
    roof: 'site_measure_truss',
    wall: 'site_measure_wall',
    floor: 'site_measure_floor',
};

/**
 * Fetch scheduled site measure jobs organized by weekday.
 * Produces one card per qualifying required quote that has site_measure_date set.
 * E.g. a job with wall + floor site measure required → 2 separate cards.
 */
export const getSiteMeasureScheduled = async (companyId, filters = {}) => {
    try {
        const { data, error } = await siteMeasureBaseQuery(companyId);
        if (error) throw error;

        let monday;
        if (filters.weekStart) {
            monday = new Date(filters.weekStart);
        } else {
            const today = new Date(new Date().toLocaleString('en-US', { timeZone: 'Australia/Melbourne' }));
            monday = new Date(today);
            monday.setDate(today.getDate() - today.getDay() + 1);
        }
        monday.setUTCHours(0, 0, 0, 0);

        const organized = { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] };

        (data || []).forEach(job => {
            const initialQuotes = (job.job_quotes || []).filter(q =>
                q.delivery_count === null || q.delivery_count === undefined || q.delivery_count === 0
            );

            // One card per qualifying required quote that has a site_measure_date
            for (const [smType, flagName] of Object.entries(SM_TYPE_FLAG)) {
                if (!job[flagName]) continue;
                const matchingQuotes = initialQuotes.filter(q =>
                    q.is_required && q.quote_type === smType && q.site_measure_date
                );
                for (const quote of matchingQuotes) {
                    // site_measure_date is a 'date' PG column → 'YYYY-MM-DD' string
                    const [y, m, d] = quote.site_measure_date.split('-').map(Number);
                    const jobDate = new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0));
                    const diffDays = Math.floor((jobDate.getTime() - monday.getTime()) / (1000 * 60 * 60 * 24));
                    if (diffDays >= 0 && diffDays <= 6) {
                        const dayOfWeek = jobDate.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
                        if (organized[dayOfWeek]) {
                            organized[dayOfWeek].push({
                                ...job,
                                job_quotes: initialQuotes,
                                schedule_row_id: quote.child_unique_id || job.unique_id,
                                schedule_row_uuid: quote.id,
                                site_measure_date: quote.site_measure_date,
                                quote_info: quote,
                                sm_type: smType,
                            });
                        }
                    }
                }
            }
        });

        return { data: organized, error: null };
    } catch (error) {
        console.error('Error fetching site measure scheduled:', error);
        return {
            data: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
            error,
        };
    }
};

/**
 * Fetch unscheduled site measure jobs (site_measure_required=true but no site_measure_date
 * on any required quote).
 */
export const getSiteMeasureUnscheduled = async (companyId) => {
    try {
        const { data, error } = await siteMeasureBaseQuery(companyId);
        if (error) throw error;

        const processedData = [];

        (data || []).forEach(job => {
            const initialQuotes = (job.job_quotes || []).filter(q =>
                q.delivery_count === null || q.delivery_count === undefined || q.delivery_count === 0
            );

            // One card per qualifying required quote WITHOUT a site_measure_date
            for (const [smType, flagName] of Object.entries(SM_TYPE_FLAG)) {
                if (!job[flagName]) continue;
                const matchingQuotes = initialQuotes.filter(q =>
                    q.is_required && q.quote_type === smType && !q.site_measure_date
                );
                for (const quote of matchingQuotes) {
                    processedData.push({
                        ...job,
                        job_quotes: initialQuotes,
                        schedule_row_id: quote.child_unique_id || job.unique_id,
                        schedule_row_uuid: quote.id,
                        site_measure_date: null,
                        quote_info: quote,
                        sm_type: smType,
                    });
                }
            }
        });

        return { data: processedData, error: null };
    } catch (error) {
        console.error('Error fetching unscheduled site measure jobs:', error);
        return { data: [], error };
    }
};

/**
 * Update site_measure_date on ALL required job_quotes for a job.
 * Pass null to clear the date (unschedule).
 */
/**
 * Set or clear the site_measure_confirmed flag on a specific job_quotes row.
 * @param {string} quoteId - job_quotes.id (UUID)
 * @param {boolean} value
 */
export const updateSiteMeasureConfirmed = async (quoteId, value) => {
    try {
        const { data, error } = await supabase
            .from('job_quotes')
            .update({ site_measure_confirmed: value, updated_at: new Date().toISOString() })
            .eq('id', quoteId)
            .select();
        if (error) throw error;
        return { data, error: null };
    } catch (error) {
        console.error('Error updating site_measure_confirmed:', error);
        return { data: null, error };
    }
};

export const updateSiteMeasureDate = async (quoteId, newDate) => {
    try {
        let dateValue = null;
        if (newDate) {
            const y = newDate.getUTCFullYear();
            const m = String(newDate.getUTCMonth() + 1).padStart(2, '0');
            const d = String(newDate.getUTCDate()).padStart(2, '0');
            dateValue = `${y}-${m}-${d}`;
        }
        const { data, error } = await supabase
            .from('job_quotes')
            .update({ site_measure_date: dateValue, updated_at: new Date().toISOString() })
            .eq('id', quoteId)
            .select();
        if (error) throw error;
        return { data, error: null };
    } catch (error) {
        console.error('Error updating site measure date:', error);
        return { data: null, error };
    }
};
